/**
 * API client for Bruce — Alfred's chat assistant. Backend: oh_assistant Django
 * app, endpoints under /oh_assistant/alfred/assistant/*. Access is decided
 * server-side; `fetchBruceConfig` returns { enabled } and the widget renders
 * nothing when it is false. Every `apiEndpoints.X` reference lives INSIDE a
 * function body (TDZ-safety) and every fn returns `response.data`.
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { get, post } from '../request'

const fetchBruceConfig = async () => {
  try {
    const response = await get(apiEndpoints.bruceConfig)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching bruce config')
    throw error
  }
}

const fetchBruceHistory = async (conversation) => {
  try {
    const url = conversation
      ? `${apiEndpoints.bruceHistory}?conversation=${conversation}`
      : apiEndpoints.bruceHistory
    const response = await get(url)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching bruce history')
    throw error
  }
}

const sendBruceMessage = async (message, conversation, basis) => {
  try {
    const response = await post(apiEndpoints.bruceChat, {
      message,
      conversation: conversation || null,
      basis: basis || 'alfred'
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in sending bruce message')
    throw error
  }
}

const sendBruceFeedback = async (messageUuid, verdict) => {
  try {
    const response = await post(apiEndpoints.bruceFeedback, {
      message: messageUuid,
      verdict: verdict || ''
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in sending bruce feedback')
    throw error
  }
}

const fetchBruceAgents = async () => {
  try {
    const response = await get(apiEndpoints.bruceAgents)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching bruce agents')
    throw error
  }
}

// A tap on a card Bruce proposed: op 'save' or 'dismiss'.
const sendBruceAction = async (messageUuid, actionId, op) => {
  try {
    const response = await post(apiEndpoints.bruceActions, {
      message: messageUuid,
      action: actionId,
      op
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in sending bruce action')
    throw error
  }
}

const fetchBruceReminders = async () => {
  try {
    const response = await get(apiEndpoints.bruceReminders)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching bruce reminders')
    throw error
  }
}

// op: 'done' | 'hour' | 'tomorrow' | 'cancel'
// op: done | hour | tomorrow | cancel, or edit with { text, date, time }.
const actOnBruceReminder = async (reminderUuid, op, fields) => {
  try {
    const response = await post(
      `${apiEndpoints.bruceReminders}/${reminderUuid}`,
      { op, ...(fields || {}) }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating a bruce reminder')
    throw error
  }
}

const startBruceChat = async () => {
  try {
    const response = await post(apiEndpoints.bruceConversations, {})
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in starting a new bruce chat')
    throw error
  }
}

// The owner's "Bruce — who can use it" page. The server refuses anyone else.
const fetchBruceAccess = async () => {
  try {
    const response = await get(apiEndpoints.bruceAccess)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching bruce access list')
    throw error
  }
}

const searchBruceAccess = async (query) => {
  try {
    const url = `${apiEndpoints.bruceAccessSearch}?q=${encodeURIComponent(
      query
    )}`
    const response = await get(url)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in searching bruce access')
    throw error
  }
}

const setBruceAccess = async (user, enabled) => {
  try {
    const response = await post(apiEndpoints.bruceAccess, { user, enabled })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in switching bruce access')
    throw error
  }
}

export {
  fetchBruceAccess,
  searchBruceAccess,
  setBruceAccess,
  fetchBruceConfig,
  fetchBruceHistory,
  sendBruceMessage,
  sendBruceFeedback,
  fetchBruceAgents,
  sendBruceAction,
  fetchBruceReminders,
  actOnBruceReminder,
  startBruceChat
}
